import { computed, Injectable, signal } from '@angular/core';
import { UIPreviewKey, UIPreviewToggleService } from './ui-preview-toggle.service';

/**
 * Service responsible for managing the `UIPreviewBannerComponent` state and behavior.
 * It provides methods to show and hide the preview banner, and toggle the UI preview,
 * as well as computed properties for determining the banner's visibility and associated
 * messages.
 */
@Injectable({
  providedIn: 'root'
})
export class UIPreviewBannerService {
  /**
   * Signal representing the current previewable feature. If undefined, no preview is available.
   */
  previewKey = signal<UIPreviewKey>(undefined);

  /**
   * Signal indicating whether the preview is currently enabled.
   */
  previewEnabled = signal(false);

  /**
   * Computed property that determines whether the banner should be shown.
   * Returns `true` if a preview key is set, otherwise `false`.
   */
  public isBannerShown = computed(() => this.previewKey() !== undefined);

  /**
   * Computed property that generates the message key for the banner.
   * The key is based on the current preview key and whether the preview is enabled.
   * Returns `undefined` if no preview key is set.
   */
  public bannerMessageKey = computed(() => 
    this.previewKey() ? `ui-preview.${this.previewKey()}.${this.previewEnabled() ? 'revert' : 'try'}` : undefined
  );

  /**
   * Computed property that generates the translation key for the text on the banner action button.
   * The translation key is derived from the `bannerMessageKey`. Returns `undefined` if
   * `bannerMessageKey` is not set.
   */
  public bannerActionTextKey = computed(() => this.bannerMessageKey() ? `${this.bannerMessageKey()}-button` : undefined);

  constructor(
    private toggleService: UIPreviewToggleService
  ) {}

  /**
   * Displays the preview banner with the specified key and enabled state.
   * @param key - The key representing the preview feature.
   * @param enabled - A boolean indicating whether the preview is enabled.
   */
  showBanner(key: UIPreviewKey, enabled: boolean) {
    this.previewKey.set(key);
    this.previewEnabled.set(enabled);
  }

  /**
   * Hides the preview banner by clearing the preview key.
   */
  hideBanner() {
    this.previewKey.set(undefined);
  }

  /**
   * Toggles the preview state for the current preview key.
   * Calls the `togglePreview` method of the `UIPreviewToggleService` with the
   * current preview key and the opposite of the current enabled state.
   */
  togglePreview() {
    this.toggleService.togglePreview(this.previewKey(), !this.previewEnabled());
  }
}
